import { useState, useRef, useEffect } from 'react'
import { Plus, ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

// Click-outside + Escape to close — shared between popovers
function useDismissable(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])
  return ref
}

const POPOVER_SHADOW = '0 8px 24px -8px rgba(29,26,21,0.18)'

// ===== LinkChip — shows linked ids; click → checkbox popover ============

export interface LinkChipOption {
  idLabel: string
  text: string
  checked: boolean
}

export function LinkChip({
  ids, direction, kind, active,
  options, onToggle, popoverHeading,
}: {
  ids: string[]
  direction: 'forward' | 'back'
  kind: 'pain-reliever' | 'outcome-creator'
  active?: boolean
  options: LinkChipOption[]
  onToggle: (optionIdx: number) => void
  popoverHeading: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useDismissable(open, () => setOpen(false))

  const arrow = direction === 'forward' ? '→' : '←'
  const isEmpty = ids.length === 0
  const arrowColor = kind === 'pain-reliever' ? 'var(--color-solution-dark)' : 'var(--color-market-dark)'

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        className={cn(
          'inline-flex items-center gap-1 font-mono text-[10.5px] border rounded px-1.5 py-0.5 whitespace-nowrap transition-colors cursor-pointer hover:border-text-muted',
          active ? 'bg-white' : 'bg-warm-100 text-text-secondary',
        )}
        style={{
          borderColor: active ? 'var(--color-accent-500)' : 'var(--color-border-subtle)',
          color: active ? 'var(--color-accent-500)' : undefined,
          opacity: isEmpty && !active ? 0.55 : 1,
        }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="font-semibold"
              style={{ color: active ? 'var(--color-accent-500)' : arrowColor }}>
          {arrow}
        </span>
        <span>{isEmpty ? '—' : ids.join(', ')}</span>
        <ChevronDown className={cn('w-2.5 h-2.5 ml-px opacity-60 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full mt-1 right-0 z-30 w-[300px] bg-white border border-border rounded shadow-md overflow-hidden"
          style={{ boxShadow: POPOVER_SHADOW }}
        >
          <div className="px-3 py-2 border-b border-border-subtle bg-warm-50 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
            {popoverHeading}
          </div>
          {options.length === 0
            ? <div className="px-3 py-3 text-[12px] text-text-muted italic">Nothing to link to yet.</div>
            : (
              <ul className="max-h-[260px] overflow-y-auto py-1">
                {options.map((opt, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={opt.checked}
                      onClick={(e) => { e.stopPropagation(); onToggle(i) }}
                      className="flex items-start gap-2 w-full text-left px-3 py-1.5 hover:bg-warm-100/70 transition-colors"
                    >
                      <span
                        className="flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center mt-px"
                        style={{
                          background: opt.checked ? 'var(--color-accent-500)' : 'white',
                          borderColor: opt.checked ? 'var(--color-accent-500)' : 'var(--color-border-strong)',
                        }}
                        aria-hidden
                      >
                        {opt.checked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                      </span>
                      <span className="font-mono text-[10.5px] text-text-muted shrink-0 mt-px">{opt.idLabel}</span>
                      <span className="flex-1 text-[12px] text-text leading-snug line-clamp-2">
                        {opt.text || <em className="text-text-muted">Untitled</em>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}
    </div>
  )
}

// ===== EyebrowAdd — opens menu of targets, picks one to create ==========

export function EyebrowAdd({ label, options, onSelect, popoverHeading, disabled }: {
  label: string
  options: { idLabel: string; text: string }[]
  onSelect: (optionIdx: number) => void
  popoverHeading: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useDismissable(open, () => setOpen(false))

  const empty = options.length === 0

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled || empty}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium normal-case tracking-normal text-text-muted hover:bg-warm-100 hover:text-accent-500 transition-colors disabled:opacity-40"
      >
        <Plus className={cn('w-2.5 h-2.5 transition-transform', open && 'rotate-45')} />
        {label}
      </button>
      {open && !empty && (
        <div
          role="menu"
          className="absolute top-full mt-1 right-0 z-30 w-[300px] bg-white border border-border rounded shadow-md overflow-hidden"
          style={{ boxShadow: POPOVER_SHADOW }}
        >
          <div className="px-3 py-2 border-b border-border-subtle bg-warm-50 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
            {popoverHeading}
          </div>
          <ul className="max-h-[260px] overflow-y-auto py-1">
            {options.map((opt, i) => (
              <li key={i}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onSelect(i); setOpen(false) }}
                  className="flex items-start gap-2 w-full text-left px-3 py-1.5 hover:bg-warm-100/70 transition-colors"
                >
                  <span className="font-mono text-[10.5px] text-text-muted shrink-0 mt-px">{opt.idLabel}</span>
                  <span className="flex-1 text-[12px] text-text leading-snug line-clamp-2">
                    {opt.text || <em className="text-text-muted">Untitled</em>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
